import { PageLayout } from '@repo/ui/components/page-layout'
import { createFileRoute } from '@tanstack/react-router'
import { CloudOff, Users } from 'lucide-react'
import { useTranslation } from '@/lib/i18n'
import { NodeDetails } from '@/modules/file-manager/components/details-node'
import { DetailsPanel } from '@/modules/file-manager/components/details-panel'
import { FilePreview } from '@/modules/file-manager/components/file-preview'
import { FolderBar } from '@/modules/file-manager/components/folder-bar'
import {
	ListingError,
	ListingState
} from '@/modules/file-manager/components/listing-states'
import {
	MODIFIED_COLUMN,
	NAME_COLUMN,
	OWNER_COLUMN,
	SHARED_BY_COLUMN,
	SIZE_COLUMN
} from '@/modules/file-manager/components/node-columns'
import { NodeGrid } from '@/modules/file-manager/components/node-grid'
import { useFoldersFirst } from '@/modules/file-manager/hooks/use-folders-first'
import { ListingProvider } from '@/modules/file-manager/hooks/use-listing'
import {
	listingSearchMiddlewares,
	validateListingSearch
} from '@/modules/file-manager/lib/folder-search'
import { useSharedWithMe } from '@/modules/file-manager/query/use-shared-with-me'
import { GlobalSearch } from '@/modules/search/components/global-search'

/**
 * The second of the two roots: everything other people have given the caller
 * access to.
 *
 * **The same grid as a folder, and that is the whole point of the page.** It
 * orders itself the way a folder does, on the same four columns and with the
 * same schema, because `list-shared-with-me` takes the same `sort`
 * `list-children` does — folders-first grouping included, which is what lets
 * `ListingGrid` section the rows here as safely as it does in a folder. A list
 * this one grows without bound — every share anybody ever opened — and a name
 * that could only be found by scrolling was the section's one missing
 * affordance.
 *
 * **The same items, too, and not a pair that resembles them.** This is
 * `NodeGrid` itself — the listing every node page composes — so a single click
 * picks a row and a double click opens it exactly as it does in a folder, in
 * whichever view is on screen. Its columns add «Condiviso da» beside
 * «Proprietario»: the person who owns a root and the one who opened it to the
 * caller are not always the same person.
 *
 * **What is missing is the move, and only the move.** These rows share no
 * parent: there is nothing to move them out of and no container whose role
 * would allow it. So this page passes `parentId={null}`, and the items
 * read that answer themselves — no drag, no drop
 * target, no «Move to…» — rather than being told by a prop threaded down
 * through the grid.
 *
 * **The row between the name and the grid is here too, and it is the same
 * `FolderBar`**: the filters while nothing is picked, the selection bar while
 * something is. The absence shows there one last time — that bar carries the
 * count and the way out of it and no menu, because moving is the only bulk
 * action MS7 has and it is the one thing this page cannot offer.
 */
export const Route = createFileRoute('/(private)/(shell)/shared/')({
	component: SharedPage,
	// Canonical on the way out, forgiving on the way in: OPSSTD-6606, and
	// `folder-search.ts` holds the whole of why they are two halves.
	search: { middlewares: listingSearchMiddlewares },
	validateSearch: validateListingSearch
})

function SharedPage() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	// Not in the search: it is a stored preference rather than part of the
	// address, for the reason `use-folders-first.ts` gives. It still reaches the
	// request and still keys the cache.
	const foldersFirst = useFoldersFirst()
	const { query, roots } = useSharedWithMe(
		{
			by: search.by,
			direction: search.direction,
			foldersFirst
		},
		{ categories: search.categories, people: search.people }
	)

	return (
		/*
		 * Unkeyed, unlike the folder pages: they key on the folder so that
		 * leaving one empties the selection, and there is only ever one listing
		 * here. Re-sorting returns the same roots in another order, which is
		 * not a reason to forget what was picked.
		 */
		<ListingProvider
			nodes={roots}
			parentId={null}
			/*
			 * `null` and not «unknown»: these roots have no folder around them,
			 * so there is nothing to be allowed to write into. The move is
			 * already absent here through `parentId`, and this says the same
			 * thing about the same absence instead of leaving the question
			 * hanging.
			 */
			parentRole={null}
		>
			<PageLayout
				aside={
					<DetailsPanel>
						<NodeDetails />
					</DetailsPanel>
				}
				bar={<FolderBar />}
				breadcrumb={
					<h1 className='min-w-0 flex-1 font-medium text-base'>
						{t('client:shared.title')}
					</h1>
				}
				search={<GlobalSearch />}
			>
				<NodeGrid
					columns={SHARED_ROOT_COLUMNS}
					empty={
						<ListingState
							description={t('client:shared.empty.description')}
							icon={<Users />}
							title={t('client:shared.empty.title')}
						/>
					}
					error={
						<ListingError
							description={t('client:shared.error.description')}
							onRetry={() => query.refetch()}
							title={t('client:shared.error.title')}
						/>
					}
					filteredDescription='client:files.filters.empty.description_shared'
					nodes={roots}
					offline={
						// The title is the folder page's: a lost connection is
						// the same fact wherever it is met. Only what cannot be
						// reached because of it is this page's to say.
						<ListingState
							description={t('client:shared.offline.description')}
							icon={<CloudOff />}
							title={t('client:files.offline.title')}
						/>
					}
					query={query}
				/>
			</PageLayout>

			{/* A shared root can be a file — somebody shares one file and it
			    arrives here with no folder around it — so this page hosts the
			    viewer like the folder pages do. */}
			<FilePreview />
		</ListingProvider>
	)
}

const SHARED_ROOT_COLUMNS = [
	NAME_COLUMN,
	OWNER_COLUMN,
	SHARED_BY_COLUMN,
	SIZE_COLUMN,
	MODIFIED_COLUMN
]
